import { useQuery, queryOptions } from "@tanstack/react-query";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import {
  scheduledShiftSchema,
  medicalCompetenceSchema,
  employee,
} from "@medoma/models/shifts";
import { machineFormat } from "@medoma/models/date-and-time";
import type { SiteId } from "@medoma/models/sites";
import { z } from "zod";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { fetchPatients, resolvePatient } from "./Patients";
import { categorySchema, isGroup } from "@medoma/models/activities";
import { useUserProfile } from "./Users";
import { unenrichedShiftWithContentsSchema } from "@medoma/models/shiftWithContents.unenriched";

const shiftKeys = {
  all: ["shifts"] as const,
  lists: () => [...shiftKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), filters] as const,
  listWithContents: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), "contents", filters] as const,
  listForSite: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), "for-site", filters] as const,
};

async function fetchEnabledCompetences() {
  const parsedEnabledCompetences = await logisticsApi
    .get("/competences")
    .json(z.array(medicalCompetenceSchema));

  return parsedEnabledCompetences;
}

const competenceKeys = {
  all: ["enabledCompetences"] as const,
};

export const useEnabledCompetences = () => {
  return useQuery({
    queryKey: competenceKeys.all,
    queryFn: fetchEnabledCompetences,
  });
};

async function fetchShiftsWithContents(date: string) {
  const parsedUnenrichedShiftsWithContents = await logisticsApi
    .get(`/ambulating/schedule/planned${generateQueryString({ date })}`)
    .json(z.array(unenrichedShiftWithContentsSchema));

  // AdminTask activities sometimes miss patientId
  const patientIds = deduplicatePrimitiveArray(
    parsedUnenrichedShiftsWithContents.flatMap(({ items }) =>
      items.flatMap((item) => {
        const isRoute = "visits" in item;
        if (isRoute) {
          return item.visits.map((visit) => visit.patientId);
        }
        const isActivityOccurrenceWithNoPatient = !("patientId" in item);
        if (isActivityOccurrenceWithNoPatient || !item.patientId) {
          return [];
        }
        return [item.patientId];
      }),
    ),
  );

  const patients = await fetchPatients({ patientIds });

  const enrichedShifts = parsedUnenrichedShiftsWithContents.map(
    (unenrichedShift) => ({
      ...unenrichedShift,
      items: unenrichedShift.items.map((item) => {
        if ("patientId" in item) {
          // An admin task is the only category that can come without a patient.
          if (item.category === categorySchema.enum.AdminTask) {
            const { patientId, ...adminTask } = item;
            return {
              ...adminTask,
              patient: patientId
                ? resolvePatient({ patientId, patients })
                : null,
            };
          }

          const { patientId, ...occurrenceOrGroup } = item;
          const patient = resolvePatient({ patientId, patients });
          if (isGroup(occurrenceOrGroup)) {
            return {
              ...occurrenceOrGroup,
              patient,
              occurrences: occurrenceOrGroup.occurrences.map(
                ({ patientId: _droppedPatientId, ...occurrence }) => ({
                  ...occurrence,
                  patient,
                }),
              ),
            };
          }
          return {
            ...occurrenceOrGroup,
            patient,
          };
        }

        if ("visits" in item) {
          return {
            ...item,
            visits: item.visits.map(({ patientId, ...visit }) => ({
              ...visit,
              patient: resolvePatient({ patientId, patients }),
            })),
          };
        }

        return item;
      }),
    }),
  );

  return enrichedShifts;
}

const shiftsWithContentsQueryOptions = (selectedDate: Date) => {
  const formattedDate = machineFormat(selectedDate, "yyyy-MM-dd");

  return queryOptions({
    queryKey: shiftKeys.listWithContents({
      date: formattedDate,
    }),
    queryFn: () => fetchShiftsWithContents(formattedDate),
  });
};

export const useShiftsWithContents = (selectedDate: Date) => {
  // This fetches data for all shifts. The goal is to only fetch data for "my" shift
  // Refactor when BE in place for this (new endpoint)
  return useQuery(shiftsWithContentsQueryOptions(selectedDate));
};

export const useMyRoutesAndActivities = (selectedDate: Date) => {
  const { employeeNumber } = useUserProfile();
  const myEmployeeId = employee.shape.id.parse(Number(employeeNumber));
  return useQuery({
    ...shiftsWithContentsQueryOptions(selectedDate),
    select: (shifts) =>
      shifts
        .filter(
          (shiftWithContents) =>
            shiftWithContents.shift.employee?.id === myEmployeeId,
        )
        .sort(
          (a, b) =>
            a.shift.startDateTime.getTime() - b.shift.startDateTime.getTime(),
        )
        .flatMap((shiftWithContents) => shiftWithContents.items),
  });
};

async function fetchShiftsForSite(date: string, siteId: SiteId) {
  return logisticsApi
    .get(`/ambulating/schedule${generateQueryString({ date, siteId })}`)
    .json(z.array(scheduledShiftSchema));
}

const shiftsForSiteQueryOptions = (selectedDate: Date, siteId: SiteId) => {
  const formattedDate = machineFormat(selectedDate, "yyyy-MM-dd");

  return queryOptions({
    queryKey: shiftKeys.listForSite({ date: formattedDate, siteId }),
    queryFn: () => fetchShiftsForSite(formattedDate, siteId),
  });
};

export const useShiftsForSite = (selectedDate: Date, siteId: SiteId) => {
  return useQuery(shiftsForSiteQueryOptions(selectedDate, siteId));
};

export async function assignGroupToShift(groupId: string, shiftId: number) {
  await logisticsApi.post(`/ambulating/shifts/${shiftId}/assign`, {
    json: { occurrenceGroupId: groupId },
  });
}

export async function unassignGroupFromShift(groupId: string, shiftId: number) {
  await logisticsApi.post(`/ambulating/shifts/${shiftId}/unassign`, {
    json: { occurrenceGroupId: groupId },
  });
}
